import React, { useMemo, useState } from 'react';
import DOMPurify from 'dompurify';

const VIEWPORTS = {
    desktop: { width: '100%', label: 'Desktop' },
    tablet: { width: '768px', label: 'Tablet' },
    mobile: { width: '375px', label: 'Mobile' },
};

const toPlainText = (html) =>
    (html || '')
        .replace(/<style[\s\S]*?<\/style>/gi, '')
        .replace(/(?:<a[^>]*href=["']([^"']+)["'][^>]*>)(.*?)(?:<\/a>)/gi, '$2 [$1]')
        .replace(/<[^>]*>/g, '')
        .replace(/\n{3,}/g, '\n\n')
        .trim();

/**
 * Renders the composed email across desktop/tablet/mobile widths, light/dark themes,
 * plus raw HTML and plain-text tabs. No real email-client rendering engine — a
 * fixed-width simulated container, per Phase 1 scope.
 */
const PreviewPanel = ({ subject, html, onSendTest }) => {
    const [viewport, setViewport] = useState('desktop');
    const [theme, setTheme] = useState('light');
    const [tab, setTab] = useState('rendered'); // rendered | raw | plain

    const sanitizedHtml = useMemo(() => DOMPurify.sanitize(html || ''), [html]);
    const plainText = useMemo(() => toPlainText(html), [html]);

    return (
        <div className="admin-card mb-3">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <h5 className="mb-0"><i className="bi bi-eye me-2"></i>Preview</h5>
                <div className="btn-group btn-group-sm" role="group">
                    <button type="button" className={`btn ${tab === 'rendered' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setTab('rendered')}>Rendered</button>
                    <button type="button" className={`btn ${tab === 'raw' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setTab('raw')}>Raw HTML</button>
                    <button type="button" className={`btn ${tab === 'plain' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setTab('plain')}>Plain Text</button>
                </div>
            </div>

            {tab === 'rendered' && (
                <>
                    <div className="d-flex justify-content-between flex-wrap gap-2 mb-2">
                        <div className="btn-group btn-group-sm" role="group">
                            {Object.entries(VIEWPORTS).map(([key, v]) => (
                                <button key={key} type="button" className={`btn ${viewport === key ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setViewport(key)}>
                                    {v.label}
                                </button>
                            ))}
                        </div>
                        <div className="btn-group btn-group-sm" role="group">
                            <button type="button" className={`btn ${theme === 'light' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setTheme('light')}>
                                <i className="bi bi-sun me-1"></i>Light
                            </button>
                            <button type="button" className={`btn ${theme === 'dark' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setTheme('dark')}>
                                <i className="bi bi-moon me-1"></i>Dark
                            </button>
                        </div>
                    </div>
                    <div className="fw-semibold mb-2">Subject: {subject || <span className="text-muted">(no subject)</span>}</div>
                    <div
                        className={`email-preview-frame theme-${theme}`}
                        style={{ width: VIEWPORTS[viewport].width, maxWidth: '100%' }}
                    >
                        <div dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
                    </div>
                </>
            )}

            {tab === 'raw' && (
                <pre className="bg-light p-3 rounded" style={{ maxHeight: '400px', overflow: 'auto', fontSize: '12px' }}>
                    {sanitizedHtml}
                </pre>
            )}

            {tab === 'plain' && (
                <pre className="bg-light p-3 rounded" style={{ maxHeight: '400px', overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                    {plainText}
                </pre>
            )}

            {onSendTest && (
                <button type="button" className="btn btn-outline-secondary btn-sm mt-3" onClick={onSendTest}>
                    <i className="bi bi-send-check me-1"></i> Send Test Email to Myself
                </button>
            )}
        </div>
    );
};

export default PreviewPanel;
